import styled from '@emotion/styled';

export const Wrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 101;
  box-sizing: border-box;
  width: 100%;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${({ theme }) => theme.colors.neutrals.white};
  ${({ theme }) => theme.media.showAt({ to: 2 })`
    padding: 0 ${theme.spacing(2)};
    height: 64px;
  `};
  ${({ theme }) => theme.media.showAt({ from: 3 })`
    padding: 0 72px;
  `};
`;

// second sticky row directly under the header (confirmed via Figma: the ticker instance sits
// flush against the header's own box, no gap). top mirrors Wrapper's own responsive height
// exactly (64px mobile / 80px desktop) so it sits flush under whichever header height is
// actually rendered — MarketTrendTicker itself is rendered with sticky={false} since this
// wrapper supplies the (header-aware) stickiness instead.
export const TickerStickyWrapper = styled.div`
  position: sticky;
  z-index: 100;
  width: 100%;
  ${({ theme }) => theme.media.showAt({ to: 2 })`
    top: 64px;
  `};
  ${({ theme }) => theme.media.showAt({ from: 3 })`
    top: 80px;
  `};
`;

// logo zone — rendered FIRST in the header so it lands physically RIGHT under RTL (matches Figma).
// The shared Logo component's full-size SVG renders at its own native ~118x24 — sized up here to
// match this header's confirmed 150x31 Figma instance without touching the shared component
export const LogoZone = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;

  svg {
    width: 150px;
    height: 31px;
  }
`;

// actions zone (avatar + saves) — rendered LAST so it lands physically LEFT under RTL
export const ActionsZone = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

// a normal flex child (not absolutely positioned) sitting between LogoZone and ActionsZone —
// Wrapper's own justify-content:space-between gives it equal breathing room on both sides;
// min-width:0 lets it shrink below its content's natural width instead of overflowing the header
// at narrow widths (Wrapper's flex-shrink defaults already allow this, this just removes the
// content-based floor)
export const CenterSlot = styled.div`
  width: 100%;
  max-width: 670px;
  min-width: 0;
  margin: 0 ${({ theme }) => theme.spacing(3)};
`;

export const SavesButton = styled.button`
  flex-shrink: 0;
  height: 48px;
  padding: 10px 16px 10px 24px;
  border: 1px solid ${({ theme }) => theme.colors.neutrals.grey6};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  background: ${({ theme }) => theme.colors.neutrals.white};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(1)};
  color: ${({ theme }) => theme.colors.neutrals.grey1};
  font-weight: 700;
  font-family: NovemberHebrew;
  font-size: 16px;
  line-height: 22px;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.neutrals.grey1};
  }
`;

// logged-out (visitor) desktop header CTA — replaces the avatar + saves cluster. Confirmed via
// Figma (node 11156:53511, the "Avatar" component's own "login" variant): a fixed 118x48 white
// pill matching the logged-in avatar chip's own shape, not a solid-black button.
export const HeaderLoginButton = styled.button`
  flex-shrink: 0;
  width: 118px;
  height: 48px;
  padding: 4px 6px 4px 4px;
  border: 1px solid ${({ theme }) => theme.colors.neutrals.grey6};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  background: ${({ theme }) => theme.colors.neutrals.white};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.neutrals.grey1};
  font-weight: 700;
  font-family: NovemberHebrew;
  font-size: 16px;
  line-height: 22px;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.neutrals.grey1};
  }
`;

// bookmark.svg's real viewBox (13.5x18.75) is a tall, narrow ribbon, not square — forcing
// width:height to equal values here would squash it. width-only + height:auto keeps its true
// proportions. Bumped up from the earlier 12px width per explicit feedback that button icons
// across the investor redesign read too small.
export const SavesIcon = styled.div`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  > svg {
    width: 14px;
    height: auto;
  }
`;

// mobile: same absolute-centering technique as CenterSlot, since the leading (avatar) and
// trailing (search toggle) buttons are both 48px today but shouldn't need to stay equal-width
// for the logo to stay centered
export const MobileLogoSlot = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);

  // full logo (icon + "madlan" wordmark, same asset LogoZone uses) sized to Figma's confirmed
  // 130x26 mobile instance (11164:94122) via the same CSS-sizing technique LogoZone already
  // establishes for this shared, size-prop-less Logo component
  svg {
    width: 130px;
    height: 26px;
  }
`;

// used for the mobile search-toggle button — investors-mobile-header-search.svg (pixel-exact,
// sourced from Figma 11164:94117) already bakes in the correct #303030 stroke, no override needed
export const MobileIconButton = styled.button`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border: 1px solid ${({ theme }) => theme.colors.neutrals.grey6};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  background: ${({ theme }) => theme.colors.neutrals.white};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.neutrals.grey1};
  }

  > svg {
    width: 24px;
    height: 24px;
  }
`;

export const MobileSearchRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

export const MobileSearchSlot = styled.div`
  flex: 1 0 0;
  min-width: 0;
`;

export const MobileCancelButton = styled.button`
  flex-shrink: 0;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.neutrals.grey1};
  font-family: NovemberHebrew;
  font-size: 16px;
  line-height: 22px;
  white-space: nowrap;
`;
